import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Check, Building2, Globe, ShieldCheck, Loader2, SquareTerminal, ChevronDown } from 'lucide-react';
import WordIcon from '../../../components/icons/WordIcon';
import { fetchAIProfileByTask, updateAIProfile, createAIProfile } from '../../../Api/aiProfileApi';
import { setAIProfile } from '../../../slice/aiProfileSlice';
import { useThemeColor } from '../../../hooks/useThemeColor';
import { useToast } from '../../../components/Toast';
import { useTranslation } from '../../../i18n/LanguageContext';

// PDF spec doesn't mandate an exact number, only that a document itself runs
// 5,000-8,000 tokens and the default should be "generous" — set explicitly
// per product decision. Below that range, longer documents can truncate.
// GPT-5 reasoning tokens draw from this same budget, hence the headroom above 8,000.
const MAX_OUTPUT_TOKENS_DEFAULT = 16000;
const MAX_OUTPUT_TOKENS_MIN = 100;
const MAX_OUTPUT_TOKENS_MAX = 65536;

const clampMaxOutputTokens = (raw) => {
  const parsed = parseInt(raw, 10);
  if (isNaN(parsed) || parsed < MAX_OUTPUT_TOKENS_MIN) return MAX_OUTPUT_TOKENS_DEFAULT;
  return Math.min(parsed, MAX_OUTPUT_TOKENS_MAX);
};

const isCodeInterpreterOn = (profile) => Boolean(profile?.code_interpreter_enabled);

const ciMaxTokensPayload = (raw) => {
  const parsed = parseInt(raw, 10);
  if (isNaN(parsed) || parsed <= 0) return 0;
  return Math.min(Math.max(parsed, MAX_OUTPUT_TOKENS_MIN), MAX_OUTPUT_TOKENS_MAX);
};

const AgentTools = ({ taskId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { aispaceColor } = useThemeColor();
  const { addToast } = useToast();

  const { data } = useSelector((state) => state.admin);
  const isAdmin = data?.user_role === 'ADMIN';
  const isSuperAdmin = data?.user_role === 'SUPER_ADMIN';
  const canEdit = isSuperAdmin;

  const [profile, setProfile] = useState(null);
  const [wordEnabled, setWordEnabled] = useState(false);
  // Empty by default, same as system_prompt_documents/_web/_hybrid on the AI
  // Profile tab — the admin writes this themselves.
  const [wordInstruction, setWordInstruction] = useState('');
  const [wordMaxOutputTokens, setWordMaxOutputTokens] = useState(String(MAX_OUTPUT_TOKENS_DEFAULT));
  const [codeInterpreterEnabled, setCodeInterpreterEnabled] = useState(false);
  const [ciInstruction, setCiInstruction] = useState('');
  const [ciMaxOutputTokens, setCiMaxOutputTokens] = useState('');
  const [ciSessionScope, setCiSessionScope] = useState('session');
  const [isScopeDropdownOpen, setIsScopeDropdownOpen] = useState(false);
  const scopeDropdownRef = useRef(null);

  useEffect(() => {
    if (!isScopeDropdownOpen) return undefined;
    const handleClickOutside = (event) => {
      if (scopeDropdownRef.current && !scopeDropdownRef.current.contains(event.target)) setIsScopeDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isScopeDropdownOpen]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!taskId) return;
    setIsLoading(true);
    fetchAIProfileByTask(taskId)
      .then((fetched) => {
        setProfile(fetched);
        setWordEnabled(fetched ? Boolean(fetched.word_enabled) : true);
        setWordInstruction(fetched?.word_instruction || '');
        setWordMaxOutputTokens(String(fetched?.word_max_output_tokens || MAX_OUTPUT_TOKENS_DEFAULT));
        setCodeInterpreterEnabled(isCodeInterpreterOn(fetched));
        setCiInstruction(fetched?.code_interpreter_instruction || '');
        setCiMaxOutputTokens(fetched?.code_interpreter_max_output_tokens ? String(fetched.code_interpreter_max_output_tokens) : '');
        setCiSessionScope(fetched?.code_interpreter_session_scope || 'session');
      })
      .catch((error) => {
        console.error('Failed to fetch AI profile for Tools tab:', error);
      })
      .finally(() => setIsLoading(false));
  }, [taskId]);

  const isDirty = profile != null ? (
    Boolean(profile.word_enabled) !== wordEnabled ||
    (profile.word_instruction || '') !== wordInstruction ||
    String(profile.word_max_output_tokens || MAX_OUTPUT_TOKENS_DEFAULT) !== wordMaxOutputTokens ||
    isCodeInterpreterOn(profile) !== codeInterpreterEnabled ||
    (profile.code_interpreter_instruction || '') !== ciInstruction ||
    (profile.code_interpreter_max_output_tokens ? String(profile.code_interpreter_max_output_tokens) : '') !== ciMaxOutputTokens ||
    (profile.code_interpreter_session_scope || 'session') !== ciSessionScope
  ) : (codeInterpreterEnabled || !wordEnabled || ciInstruction !== '' || ciMaxOutputTokens !== '' || ciSessionScope !== 'session');

  const handleSave = async () => {
    if (!canEdit || !taskId) return;
    setIsSaving(true);
    try {
      const payload = {
        task_id: taskId,
        word_enabled: wordEnabled,
        word_instruction: wordInstruction,
        word_max_output_tokens: clampMaxOutputTokens(wordMaxOutputTokens),
        code_interpreter_enabled: codeInterpreterEnabled,
        code_interpreter_instruction: ciInstruction,
        code_interpreter_max_output_tokens: ciMaxTokensPayload(ciMaxOutputTokens),
        code_interpreter_session_scope: ciSessionScope,
      };
      if (profile?.id) {
        await updateAIProfile({ ...payload, id: profile.id });
      } else {
        await createAIProfile({ ...payload, name: `AI Profile - ${taskId}` });
      }
      const refreshed = await fetchAIProfileByTask(taskId);
      setProfile(refreshed);
      if (refreshed) {
        setWordEnabled(Boolean(refreshed.word_enabled));
        setWordInstruction(refreshed.word_instruction || '');
        setWordMaxOutputTokens(String(refreshed.word_max_output_tokens || MAX_OUTPUT_TOKENS_DEFAULT));
        setCodeInterpreterEnabled(isCodeInterpreterOn(refreshed));
        setCiInstruction(refreshed.code_interpreter_instruction || '');
        setCiMaxOutputTokens(refreshed.code_interpreter_max_output_tokens ? String(refreshed.code_interpreter_max_output_tokens) : '');
        setCiSessionScope(refreshed.code_interpreter_session_scope || 'session');
        dispatch(setAIProfile(refreshed));
      }
      addToast(t('agentSettings.aiProfileSaveSuccess'), 'success');
    } catch (error) {
      console.error('Failed to save Word generation settings:', error);
      addToast(error?.response?.data?.detail || t('agentSettings.aiProfileSaveError'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-[#EEEEEE] flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: aispaceColor }} />
      </div>
    );
  }

  const fixedLayoutCards = [
    { icon: Building2, title: t('agentSettings.toolsBrandingCardTitle'), text: t('agentSettings.toolsBrandingCardText') },
    { icon: Globe, title: t('agentSettings.toolsSourcesCardTitle'), text: t('agentSettings.toolsSourcesCardText') },
    { icon: ShieldCheck, title: t('agentSettings.toolsTransparencyCardTitle'), text: t('agentSettings.toolsTransparencyCardText') },
  ];

  const sessionScopeOptions = [
    { value: 'session', label: t('agentSettings.toolsCiSessionScopeSession'), description: t('agentSettings.toolsCiSessionScopeSessionDesc') },
    { value: 'request', label: t('agentSettings.toolsCiSessionScopeRequest'), description: t('agentSettings.toolsCiSessionScopeRequestDesc') },
  ];

  const behaviourItems = [
    t('agentSettings.toolsBehaviourItem1'),
    t('agentSettings.toolsBehaviourItem2'),
    t('agentSettings.toolsBehaviourItem3'),
    t('agentSettings.toolsBehaviourItem4'),
  ];

  return (
    <div className="p-6 space-y-6 w-full min-w-0">
      <div>
        <h1 className="text-[20px] font-semibold text-[#111827]">{t('agentSettings.toolsTitle')}</h1>
        <p className="text-[14px] text-[#6B7280] mt-1">{t('agentSettings.toolsSubtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
        <div className="space-y-6 min-w-0">
        {/* Word generation card */}
        <div className="bg-white rounded-xl border border-[#EEEEEE]">
          <div className="flex items-start gap-4 p-6">
            <div className="w-10 h-10 flex-shrink-0">
              <WordIcon size={40} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[16px] font-semibold text-[#111827]">{t('agentSettings.toolsWordGenTitle')}</h3>
              <p className="text-[14px] text-[#6B7280] mt-0.5">{t('agentSettings.toolsWordGenSubtitle')}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={wordEnabled}
              disabled={!canEdit}
              onClick={() => canEdit && setWordEnabled((prev) => !prev)}
              className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: wordEnabled ? aispaceColor : '#E5E7EB' }}
            >
              <span
                className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                style={{ transform: wordEnabled ? 'translateX(22px)' : 'translateX(2px)' }}
              />
            </button>
          </div>
          <div className="border-b border-[#EEEEEE]"></div>
          <div className="p-6 space-y-5">
            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">
                {t('agentSettings.toolsInstructionLabel')}
              </label>
              <textarea
                value={wordInstruction}
                onChange={(e) => canEdit && setWordInstruction(e.target.value)}
                disabled={!canEdit}
                rows={8}
                placeholder={t('agentSettings.toolsInstructionPlaceholder')}
                className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed resize-y"
                style={{ '--focus-color': aispaceColor }}
              />
              <p className="text-[13px] text-[#9CA3AF] mt-2">{t('agentSettings.toolsInstructionHelp')}</p>
            </div>

            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">
                {t('agentSettings.toolsMaxOutputTokensLabel')}
              </label>
              <input
                type="number"
                value={wordMaxOutputTokens}
                onChange={(e) => canEdit && setWordMaxOutputTokens(e.target.value)}
                disabled={!canEdit}
                placeholder={String(MAX_OUTPUT_TOKENS_DEFAULT)}
                min={MAX_OUTPUT_TOKENS_MIN}
                max={MAX_OUTPUT_TOKENS_MAX}
                className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                style={{ '--focus-color': aispaceColor }}
              />
              <p className="text-[13px] text-[#9CA3AF] mt-2">{t('agentSettings.toolsMaxOutputTokensHelp')}</p>
            </div>

            <div className="rounded-xl border border-[#EEEEEE] p-5">
              <h4 className="text-[14px] font-semibold text-[#111827] mb-4">{t('agentSettings.toolsFixedLayoutTitle')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {fixedLayoutCards.map((card) => (
                  <div key={card.title} className="border border-[#EEEEEE] rounded-xl p-4">
                    <card.icon size={16} style={{ color: aispaceColor }} className="mb-2" />
                    <h5 className="text-[13px] font-semibold text-[#111827]">{card.title}</h5>
                    <p className="text-[12px] text-[#6B7280] mt-1">{card.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-[13px] text-[#9CA3AF] mt-4">{t('agentSettings.toolsLayoutFooterNote')}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#EEEEEE]">
          <div className="flex items-start gap-4 p-6">
            <div
              className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg"
              style={{ backgroundColor: `${aispaceColor}1A`, color: aispaceColor }}
            >
              <SquareTerminal size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[16px] font-semibold text-[#111827]">{t('agentSettings.toolsCodeInterpreterTitle')}</h3>
              <p className="text-[14px] text-[#6B7280] mt-0.5">{t('agentSettings.toolsCodeInterpreterSubtitle')}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={codeInterpreterEnabled}
              disabled={!canEdit}
              onClick={() => canEdit && setCodeInterpreterEnabled((prev) => !prev)}
              className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: codeInterpreterEnabled ? aispaceColor : '#E5E7EB' }}
            >
              <span
                className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                style={{ transform: codeInterpreterEnabled ? 'translateX(22px)' : 'translateX(2px)' }}
              />
            </button>
          </div>
          <div className="border-b border-[#EEEEEE]"></div>
          <div className="p-6 space-y-5">
            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">
                {t('agentSettings.toolsCiInstructionLabel')}
              </label>
              <textarea
                value={ciInstruction}
                onChange={(e) => canEdit && setCiInstruction(e.target.value)}
                disabled={!canEdit}
                rows={5}
                placeholder={t('agentSettings.toolsCiInstructionPlaceholder')}
                className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed resize-y"
                style={{ '--focus-color': aispaceColor }}
              />
              <p className="text-[13px] text-[#9CA3AF] mt-2">{t('agentSettings.toolsCiInstructionHelp')}</p>
            </div>

            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">
                {t('agentSettings.toolsCiMaxOutputTokensLabel')}
              </label>
              <input
                type="number"
                value={ciMaxOutputTokens}
                onChange={(e) => canEdit && setCiMaxOutputTokens(e.target.value)}
                disabled={!canEdit}
                placeholder={t('agentSettings.toolsCiMaxOutputTokensPlaceholder')}
                min={MAX_OUTPUT_TOKENS_MIN}
                max={MAX_OUTPUT_TOKENS_MAX}
                className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                style={{ '--focus-color': aispaceColor }}
              />
              <p className="text-[13px] text-[#9CA3AF] mt-2">{t('agentSettings.toolsCiMaxOutputTokensHelp')}</p>
            </div>

            <div>
              <label className="block text-[14px] font-medium text-[#374151] mb-2">
                {t('agentSettings.toolsCiSessionScopeLabel')}
              </label>
              <div className="relative" ref={scopeDropdownRef}>
                <button
                  type="button"
                  onClick={() => canEdit && setIsScopeDropdownOpen((open) => !open)}
                  disabled={!canEdit}
                  className="w-full px-4 py-3 pr-10 text-[14px] text-left border border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[var(--focus-color)] disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                  style={{ '--focus-color': aispaceColor }}
                >
                  {sessionScopeOptions.find((o) => o.value === ciSessionScope)?.label}
                </button>
                <ChevronDown size={18} className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6B7280] transition-transform ${isScopeDropdownOpen ? 'rotate-180' : ''}`} />
                {isScopeDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-20 overflow-hidden">
                    {sessionScopeOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setCiSessionScope(option.value);
                          setIsScopeDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-3 text-left text-[14px] hover:bg-[#F9FAFB] transition-colors ${
                          ciSessionScope === option.value ? 'bg-[#F3F4F6] font-medium' : 'text-[#374151]'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-[13px] text-[#9CA3AF] mt-2">{sessionScopeOptions.find((o) => o.value === ciSessionScope)?.description}</p>
            </div>
          </div>
        </div>
        </div>

        {/* Word generation behaviour side panel */}
        <div className="bg-white rounded-xl border border-[#EEEEEE] p-5">
          <h3 className="text-[14px] font-semibold text-[#111827] mb-4">{t('agentSettings.toolsBehaviourTitle')}</h3>
          <ul className="space-y-3">
            {behaviourItems.map((item) => (
              <li key={item} className="flex items-start gap-2 text-[13px] text-[#374151]">
                <Check size={16} className="text-[#10B981] flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 p-4 rounded-lg bg-[#F9FAFB] border border-[#EEEEEE]">
            <p className="text-[12px] text-[#6B7280]">
              <span className="font-semibold text-[#374151]">{t('agentSettings.toolsFutureToolsNote').split(':')[0]}:</span>
              {t('agentSettings.toolsFutureToolsNote').split(':').slice(1).join(':')}
            </p>
          </div>
        </div>
      </div>

      {(isSuperAdmin || isAdmin) && (
        <div className="flex items-center justify-end gap-3">
          {isDirty && (
            <span className="text-[13px] text-[#DC2626]">{t('agentSettings.unsavedChanges')}</span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!canEdit || !isDirty || isSaving}
            className="px-4 py-2 text-white rounded-lg text-[13px] font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            style={{ backgroundColor: aispaceColor }}
          >
            {isSaving ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                {t('agentSettings.saving')}
              </>
            ) : (
              t('agentSettings.toolsSaveButton')
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AgentTools;
